import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Link, Typography, Drawer, useTheme } from '@mui/material';
import { MainHeader } from '@eposnow/ui-core';
import TroncTable from './components/TroncTable';
import { TroncRow, TroncSummary } from '../../models/tronc';
import { GetTroncRequest, TroncApiResponse, useTroncs } from '../../api/tronc';
import { TroncFiltersContext } from '../../context/TroncFiltersContext';
import { defer, GBP } from '../../helpers/helpers';
import { UIContext } from '../../context/UIContext';
import TroncTableMobile from './components/TroncTableMobile';
import WarningBanner from '../../components/WarningBanner';
import { MILLIS_PER_DAY } from '../../helpers/dateUtils';

const SHOW_WEEKEND_NOTICE_DATE_KEY_TRONC = 'alerts.weekendNoticeTronc';

const Tronc = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isMobile, isTablet } = useContext(UIContext);
  const navigate = useNavigate();
  const { filters, isLoading: isLoadingFiltersContext, isError: isErrorFiltersContext, refetch: refetchFiltersContext } = useContext(TroncFiltersContext);
  const [page, setPage] = useState(0);
  const [troncData, setTroncData] = useState<TroncApiResponse<TroncRow> | undefined>();
  const [enableFetching, setEnableFetching] = useState<boolean>(Boolean(filters?.location?.id));
  const isWeekend = ([0, 6].includes(new Date().getDay()));
  const [showWeekendNotice, setShowWeekendNotice] = useState(true);
  const [mobileDescriptionExpanded, setMobileDescriptionExpanded] = useState<boolean>(false);

  const learnMoreLink = `${process.env.REACT_APP_LINK_SUPPORT}s/article/Tronc-Tipping-Report`;

  const linkNavigation = useCallback(
    (path: string | undefined) => {
      if (path) {
        navigate(path);
      }
    },
    [navigate]
  );

  useEffect(() => {
    const showWeekendNoticeDate = +(localStorage.getItem(SHOW_WEEKEND_NOTICE_DATE_KEY_TRONC) || '');
    setShowWeekendNotice(showWeekendNoticeDate <= Date.now());
  }, []);

  const {
    data: troncDataResponse,
    refetch: fetchTroncs,
    isFetching,
    isError,
  } = useTroncs<TroncRow>(
    {
      startDate: filters.periodFrom,
      endDate: filters.periodTo,
      location: filters?.location?.id
    } as GetTroncRequest,
    'troncs',
    {
      enabled: enableFetching,
    }
  );

  useEffect(() => {
    if (troncDataResponse) {
      setTroncData(troncDataResponse);
    }
  }, [troncDataResponse]);


  useEffect(() =>
    defer(() => {
      setPage(0);
      if (!filters.periodFrom || !filters.periodTo || !filters?.location?.id) {
        setTroncData(undefined);
        return;
      }
      setEnableFetching(true);
      fetchTroncs().then();
    }),
    [filters, fetchTroncs]
  );

  return (
    <>
        {(isMobile || isTablet) &&
          <Box
            sx={{
              marginBottom: { xs: 1, sm: 2 },
              marginX: 0,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', marginTop: '16px', marginX: '16px' }}>
              <Typography variant='h1'>
                {t('screens.troncReport.title')}
                <IconButton
                  size="small"
                  onClick={() => setMobileDescriptionExpanded(!mobileDescriptionExpanded)}
                  sx={{ verticalAlign: 'middle', color: theme.palette.text.secondary }}
                >
                  <InfoOutlined/>
                </IconButton>
              </Typography>
            </Box>
            <TroncTableMobile
              rows={troncData?.troncRows || []}
              summary={troncData?.summary || {} as TroncSummary}
              isLoading={isLoadingFiltersContext || isFetching}
              currency={troncData?.currency || GBP}
              // Display empty table on load. Only instance where filters location doesn't exist
              isError={isErrorFiltersContext || isError}
              refresh={() => { refetchFiltersContext(); fetchTroncs().then() }}
            />

            <Drawer
              anchor="bottom"
              open={mobileDescriptionExpanded}
              onClose={() => setMobileDescriptionExpanded(false)}
              sx={{
                '& .MuiDrawer-paper': {
                  padding: '24px',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                },
              }}
            >
              <Typography variant="body1">
                {t('screens.troncReport.description')}
              </Typography>
              <Typography variant="subtitle1" sx={{ textDecoration: 'underline', fontWeight: '600' }}>
                <Link href={learnMoreLink}>
                  {t('screens.troncReport.learnMore')}
                </Link>
              </Typography>
            </Drawer>
          </Box>

        }
        {!isMobile && !isTablet &&
          <>
            <Box
              sx={{
                marginLeft: 0,
                marginBottom: { xs: 1, sm: 2 },
                paddingX: { xs: 2, sm: 0 },
              }}
            >
              <MainHeader
                title={t('screens.troncReport.title')}
                nav={[
                  {
                    text: t('nav.reporting'),
                    link: '/transactions',
                    icon: <ShowChartOutlinedIcon sx={{ mr: 1 }} fontSize="inherit" />,
                    dataQaId: 'headerNavTransactionReports',
                  },
                  {
                    text: t('nav.troncReport'),
                    link: '/tronc',
                    icon: (<SummarizeOutlinedIcon sx={{ mr: 1 }} fontSize="inherit" />
                    ),
                    dataQaId: 'headerNavCompletedTroncReports'
                  },
                ]}
                theme={theme}
                linkNavigationFunction={linkNavigation}
              >
                <Typography variant="body1">
                  {t('screens.troncReport.description')}
                  <Link
                    href={learnMoreLink}
                    sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
                  >
                    {t('screens.troncReport.learnMore')}
                  </Link>
                </Typography>
              </MainHeader>
            </Box>
            <TroncTable
              rows={troncData?.troncRows || []}
              summary={troncData?.summary || {} as TroncSummary}
              isLoading={isLoadingFiltersContext || isFetching}
              currency={troncData?.currency || GBP}
              // Display empty table on load. Only instance where filters location doesn't exist
              isError={isErrorFiltersContext || isError}
              page={page}
              setPage={setPage}
              refresh={() => { refetchFiltersContext(); fetchTroncs().then() }}
            />
          </>
        }
      </>
      )
}


      export default Tronc;
